import styled from "@emotion/styled"
import { PPopover } from "@porsche-design-system/components-react"
import { durationSm, spacingStaticSm } from "@porsche-design-system/components-react/emotion"
import type { NotificationCountView, NotificationListView } from "@slfinrtl/ui-bff-routes"
import type { MouseEvent } from "react"
import { useCallback, useEffect, useState } from "react"
import useSWR from "swr"
import { useDealerAppRoutes } from "#src/common/routes/useDealerAppRoutes"
import { withErrorHandler } from "src/context/errorHandler/withErrorHandler"
import {
	trackNotificationCenterEvent,
	trackNotificationCenterPopoverEvent,
} from "./common/trackNotificationCenterEvent"
import { BellIconButton } from "./components/bellIconButton/BellIconButton"
import { NotificationPopoverContent } from "./components/notificationPopoverContent/NotificationPopoverContent"
import type { NotificationLink } from "./components/notificationPopoverContent/NotificationPopoverContent"
import { useBulkUpdateNotifications } from "./hooks/useBulkUpdateNotifications"
import { defaultNotificationCenterFilterValues, useNotificationListView } from "./hooks/useNotificationListView"
import { useNotificationOpenCount } from "./hooks/useNotificationOpenCount"
import { useUpdateNotification } from "./hooks/useUpdateNotification"

// pds popover fade-out reference: https://github.com/porsche-design-system/porsche-design-system/blob/main/packages/components/src/components/popover/popover-styles.ts
const closingDuration = Number.parseFloat(durationSm) * 1000

export const NotificationCenterPopoverContainer = withErrorHandler(() => {
	const routes = useDealerAppRoutes()

	// in order to unmount the (potentially large) notification list without cutting off the popover's
	// own closing transition, we need 2 separate states: isPopoverOpen drives the transition, while
	// isPopoverOpenWrapped keeps the content mounted until that transition has finished
	const [isPopoverOpen, setIsPopoverOpen] = useState(false)
	const [isPopoverOpenWrapped, setIsPopoverOpenWrapped] = useState(false)

	const closePopover = useCallback(() => setIsPopoverOpen(false), [])

	useEffect(() => {
		if (isPopoverOpen) {
			setIsPopoverOpenWrapped(true)
		} else if (isPopoverOpenWrapped) {
			const timeout = setTimeout(() => setIsPopoverOpenWrapped(false), closingDuration)
			return () => clearTimeout(timeout)
		}
	}, [isPopoverOpen]) // eslint-disable-line react-hooks/exhaustive-deps

	const fetchNotificationOpenCount = useNotificationOpenCount()
	const { data: { count } = {}, mutate: mutateOpenCount } = useSWR<NotificationCountView>(
		["notificationCount"],
		fetchNotificationOpenCount,
		{
			keepPreviousData: true,
			refreshInterval: 10_000, // 10s
			revalidateOnFocus: true,
			shouldRetryOnError: false,
		},
	)

	const fetchNotificationListView = useNotificationListView()
	const { data: { items } = {}, mutate: mutateListView } = useSWR<NotificationListView>(
		["notificationListView", defaultNotificationCenterFilterValues],
		fetchNotificationListView,
		{
			keepPreviousData: true,
			revalidateIfStale: false,
			revalidateOnFocus: false,
			revalidateOnMount: false,
			shouldRetryOnError: false,
		},
	)

	const bulkUpdateNotifications = useBulkUpdateNotifications()
	const onBellIconButtonClick = useCallback(() => {
		setIsPopoverOpen((prev) => !prev)
		if (isPopoverOpen) {
			// in case popover is being closed, we don't need to fetch the list or mark notifications as seen
			return
		}

		trackNotificationCenterPopoverEvent("open_popover")

		mutateListView().then((result) => {
			const ids = result?.items
				.filter((item) => item.dealerAppStatus === "OPEN")
				.map((item) => item.userNotificationId)

			if (ids?.length) {
				return Promise.all([
					bulkUpdateNotifications(ids, "SEEN"),
					mutateOpenCount(
						(current) =>
							current
								? {
										...current,
										count: Math.max(0, current.count - ids.length),
									}
								: current,
						false, // don't immediately revalidate
					),
				])
			}
		})
	}, [bulkUpdateNotifications, isPopoverOpen, mutateListView, mutateOpenCount])

	// upon navigation, e.g. link click within popover we need to close it
	const onLinkClick = useCallback((action: NotificationLink, e: MouseEvent<HTMLElement>) => {
		trackNotificationCenterPopoverEvent(action)

		const isModifiedClick =
			e.metaKey || // Cmd on macOS
			e.ctrlKey || // Ctrl on Windows/Linux
			e.shiftKey ||
			e.button === 1 // middle click

		if (!isModifiedClick) {
			setIsPopoverOpen(false)
		}
	}, [])

	const { isPending, updateNotification } = useUpdateNotification()
	const updateNotificationStatus = useCallback(
		async (userNotificationId: string, newStatus: "READ" | "SEEN") => {
			if (newStatus === "READ") {
				trackNotificationCenterEvent("mark_as_read")
			}

			await updateNotification(userNotificationId, newStatus)
			await mutateListView() // refetch immediately to avoid diverging ui state
		},
		[mutateListView, updateNotification],
	)

	return (
		<StyledPopover open={isPopoverOpen} onDismiss={closePopover}>
			<BellIconButton count={count} slot="button" onClick={onBellIconButtonClick} />
			{isPopoverOpenWrapped && (
				<StyledNotificationPopoverContent
					items={items}
					isPending={isPending}
					updateNotificationStatus={updateNotificationStatus}
					url={routes?.notificationCenter?.url()}
					onLinkClick={onLinkClick}
				/>
			)}
		</StyledPopover>
	)
})

const StyledPopover = styled(PPopover)`
	--p-popover-w: 600px;
	--p-popover-max-w: 600px;
`

const StyledNotificationPopoverContent = styled(NotificationPopoverContent)`
	padding: ${spacingStaticSm} 0;
`
